import { stringify } from 'qs';
import { useState } from 'react';
import { matchPath, useNavigate } from 'react-router-dom';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { MEMBERSHIP_PURCHASE_PROCESS_TYPES } from 'hb-react/shared/apps/Memberships/constants';
import { useShoppingCard } from 'hb-react/shared/components/ShoppingCard/hooks/useShoppingCard';

const getDraftCartCreateOrderPagePath = ({ topicContext, id, currency }) => {
  const query = stringify(
    { id, currency: currency.id },
    { addQueryPrefix: true },
  );

  const route = topicContext?.topicId
    ? Routes.topic_memberships_order_create_path(topicContext.topicId)
    : Routes.memberships_order_create_path();

  return [route, query].join('');
};

const createDraftCartAndNavigate = async ({ cartItems, create, navigate }) => {
  const {
    data: { cart },
  } = await create({ cart_items: cartItems });

  const isSingleMembership =
    matchPath(
      {
        path: '/memberships/:id',
        exact: true,
      },
      window.location.pathname,
    ) ||
    matchPath(
      {
        path: '/topics/:topicId/memberships/:id',
        exact: true,
      },
      window.location.pathname,
    );

  navigate(`${isSingleMembership ? '../' : ''}order/${cart.id}`);
};

const appendRedirectTo = ({ href, redirectTo }) =>
  `${
    href.includes('?') ? href.substr(0, href.lastIndexOf('?')) : href
  }?redirect_to=${encodeURIComponent(redirectTo)}`;

const singlePurchaseStrategy = ({
  currentUser,
  topicContext,
  navigate,
  create,
}) => {
  const onLogin = ({ id, currency }) => {
    const redirectTo = getDraftCartCreateOrderPagePath({
      id,
      currency,
      topicContext,
    });

    const loginForm = document.querySelector('.login-form [name=redirect_to]');

    if (loginForm) {
      loginForm.value = redirectTo;
    }

    const loginButton = document.querySelector('.js-login');

    const buttonSso = document.querySelector('.button__sso');

    if (buttonSso) {
      buttonSso.href = appendRedirectTo({
        href: buttonSso.href,
        redirectTo,
      });
    }

    if (loginButton) {
      loginButton.href = appendRedirectTo({
        href: loginButton.href,
        redirectTo,
      });

      return loginButton.click();
    }

    window.location = `/login?redirect_to=${redirectTo}`;
  };

  if (!currentUser) {
    return {
      onLogin,
    };
  }

  return {
    onPurchase: (membership) => {
      createDraftCartAndNavigate({
        cartItems: [membership],
        create,
        navigate,
      });
    },
  };
};

const MultiplePurchaseStrategy = ({ navigate, topicContext, create }) => {
  const [selected, setSelected] = useState([]);

  const onSelect = (membership) => {
    const { id } = membership;

    if (selected.find((m) => m.id === id)) {
      return setSelected(selected.filter((m) => m.id !== id));
    }

    setSelected([...selected, membership]);
  };

  const [firstMembership] = selected;

  const onCheckout = () => {
    createDraftCartAndNavigate({
      topicContext,
      cartItems: selected,
      create,
      navigate,
    });
  };

  const isDisabled = (membership) => {
    if (!firstMembership) {
      return false;
    }

    if (Number(membership.price) === 0) {
      return firstMembership.meta.payment.id !== membership.meta.payment.id;
    }

    return (
      firstMembership.currency.code !== membership.currency.code ||
      firstMembership.meta.payment.id !== membership.meta.payment.id
    );
  };

  return {
    onCheckout,
    onSelect,
    selectedMemberships: selected,
    isDisabled,
  };
};

export const useMembershipsPurchaseProcess = (
  type = MEMBERSHIP_PURCHASE_PROCESS_TYPES.SINGLE,
) => {
  const { currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();

  const navigate = useNavigate();

  const {
    actions: { create },
  } = useShoppingCard();

  if (type === MEMBERSHIP_PURCHASE_PROCESS_TYPES.SINGLE) {
    return singlePurchaseStrategy({
      navigate,
      currentUser,
      create,
      topicContext,
    });
  }

  if (type === MEMBERSHIP_PURCHASE_PROCESS_TYPES.MULTIPLE) {
    return MultiplePurchaseStrategy({ navigate, create });
  }

  throw new Error(`Unsupported purchase strategy ${type}`);
};
